import { createComparator, getAbsoluteUrl } from '@tmapy/utils';
import { isIncompatibilityModules } from '../utils/isIncompatibilityModules.js';
import { transformLayerConfigFromTinker } from './transformLayerConfigFromTinker.js';
import { buildLayerSwitcherConfig } from './buildLayerSwitcherConfig.js';
import { buildMapSwitcherConfig } from './buildMapSwitcherConfig.js';
import { buildRoutesConfig } from './buildRoutesConfig.js';
import { buildFeatureRouteMappings } from './buildFeatureRouteMappings.js';
import { buildInfoConfig } from './buildInfoConfig.js';
import { buildMapSearchConfig } from './buildMapSearchConfig.js';
export const COMPATIBLE_VERSION = {
    tinker: { min: '5.0.0', max: '6.0.0' },
};
const query = `
query TinkerConfig($slugId: String!) {
  installedModules {
    edges {
      node {
        version
        name
      }
    }
  }
  apps(filters: { slugId: $slugId }) {
    edges {
      node {
        id
        nameId
        origin
        title
        icon
        slugIds
        requireAuth
        loadBeaConfig
        about {
          id
          nameId
          type
          value
        }
        dictionaryEntries {
          edges {
            node {
              id
              nameId
              type
              value
            }
          }
        }
        category {
          id
          nameId
          title
          order
        }
        homeLink {
          id
          nameId
          title
          url
        }
      }
    }
  }
  maps(filters: { slugId: $slugId }) {
    edges {
      node {
        id
        nameId
        maxZoom
        minZoom
        zoom
        center
        projection
        enableLayerSwitcherFilter
        enableLayerSwitcherCustomLayers
        enableMeasure
        enableExportMap
        enableUserLocation
        enableMapNotes
        enableZoomLevelShortcuts
        enableSearch
        regionCodes
        coordsInfo
        activeWidget
      }
    }
  }
  mapSearches(first: 5000, filters: { slugId: $slugId }) {
    edges {
      node {
        id
        nameId
        title
        mapService {
          id
          nameId
          schema
          metadata
          url
          type
          matrixSet
          format
          attributions
          isAuth
          thumbnail
          geometryType
          agendaIdent
          mapGrid {
            id
            nameId
            originX
            originY
            resolutions
            extent
            tileSize
          }
        }
        fieldName
        featureTypes
        minQueryLength
        resultsLimit
        order
        label
        value
        hint {
          id
          nameId
          type
          value
        }
        isFilled
        outline
      }
    }
  }
  mapInfos(first: 5000, filters: { slugId: $slugId }) {
    edges {
      node {
        id
        nameId
        title
        order
        mapServiceLayerId
        visibilityDependencies(first: 5000) {
          edges {
            node {
              id
              nameId
            }
          }
        }
        fields
        mapService {
          id
          nameId
          schema
          metadata
          url
          type
          matrixSet
          format
          attributions
          isAuth
          thumbnail
          geometryType
          routerParams
          agendaIdent
          mapGrid {
            id
            nameId
            originX
            originY
            resolutions
            extent
            tileSize
          }
        }
      }
    }
  }
  mapLayers(first: 5000, filters: { slugId: $slugId }) {
    edges {
      node {
        id
        nameId
        title
        mapLayerGroup {
          id
          nameId
        }
        isInMapSwitcher
        isInLayerSwitcher
        isOpacitySliderVisible
        order
        opacity
        visible
        zIndex
        minResolution
        maxResolution
        mapServiceLayerIds
        mapBoxStyle
        mapBoxStyleLegend
        legendExternalLink
        legendImageLink
        canInsert
        canUpdate
        canDelete
        mapService {
          id
          nameId
          schema
          metadata
          url
          type
          matrixSet
          format
          attributions
          isAuth
          thumbnail
          geometryType
          routerParams
          agendaIdent
          mapGrid {
            id
            nameId
            originX
            originY
            resolutions
            extent
            tileSize
          }
        }
      }
    }
  }
  mapLayerGroups(first: 5000, filters: { slugId: $slugId }) {
    edges {
      node {
        id
        nameId
        title
        parent {
          id
          nameId
        }
        isExpanded
        isExclusive
        exclusiveLayers
        order
        layers(first: 5000) {
          edges {
            node {
              id
              nameId
            }
          }
        }
      }
    }
  }
  links(first: 5000, filters: { slugId: $slugId }) {
    edges {
      node {
        id
        nameId
        url
        title
      }
    }
  }
}
`;
export const loadConfigFromTinker = async (appBaseName, tinkerGraphql = '/beg/tinker/graphql') => {
    const tinkerGraphQLUrl = getAbsoluteUrl(tinkerGraphql) ?? tinkerGraphql;
    try {
        const response = await fetch(tinkerGraphQLUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                query,
                variables: {
                    slugId: appBaseName,
                },
            }),
        });
        if (!response.ok) {
            throw new Error(`Invalid response ${response.status} ${response.statusText}.`);
        }
        const responseJSON = await response.json();
        if (!responseJSON.data) {
            console.error(`Incompatible versions of tinker and client.`);
            return { appId: appBaseName, incompatibility: true };
        }
        const tinkerConfig = responseJSON.data;
        const appConfig = tinkerConfig.apps.edges[0]?.node;
        const mapConfig = tinkerConfig.maps.edges[0]?.node;
        const incompatibility = isIncompatibilityModules(tinkerConfig.installedModules.edges, COMPATIBLE_VERSION, 'Installed basic components');
        if (!appConfig) {
            throw new Error(`Application is not set in the TWC manager.`);
        }
        if (tinkerConfig.apps.edges.length > 1) {
            throw new Error(`Multiple configurations found for one application.`);
        }
        if (tinkerConfig.maps.edges.length > 1) {
            throw new Error(`Multiple configurations found for one map.`);
        }
        let view;
        if (mapConfig) {
            const { maxZoom, minZoom, zoom, center, projection } = mapConfig;
            view = {
                maxZoom,
                minZoom,
                zoom,
                center,
                projection: projection.replace('_', ':'),
            };
            view.zoom = view.zoom ?? view.minZoom;
        }
        const layers = tinkerConfig.mapLayers.edges
            .sort(createComparator((asc) => asc.node.zIndex, (asc) => asc.node.mapService.url, (asc) => asc.node.order, (asc) => asc.node.id))
            .map(transformLayerConfigFromTinker)
            .filter(Boolean);
        const isAuth = appConfig.requireAuth ||
            tinkerConfig.mapLayers.edges.some((layer) => layer.node.mapService.isAuth);
        const nonTranslatableResourceStrings = {};
        const info = buildInfoConfig(tinkerConfig, nonTranslatableResourceStrings);
        if (appConfig.title) {
            nonTranslatableResourceStrings['app.title'] = appConfig.title;
        }
        const layerSwitcher = buildLayerSwitcherConfig(tinkerConfig, nonTranslatableResourceStrings);
        const mapSwitcher = buildMapSwitcherConfig(tinkerConfig, nonTranslatableResourceStrings);
        const search = buildMapSearchConfig(tinkerConfig, nonTranslatableResourceStrings);
        const layerRoutes = buildRoutesConfig(tinkerConfig, nonTranslatableResourceStrings);
        const featureRouteMappings = buildFeatureRouteMappings(tinkerConfig);
        return {
            auth: isAuth,
            appId: appConfig.nameId,
            origin: appConfig.origin,
            loadBeaConfig: appConfig.loadBeaConfig,
            incompatibility,
            routes: layerRoutes,
            featureRouteMappings,
            map: {
                view,
                layers,
                zoomLevelShortcuts: mapConfig?.enableZoomLevelShortcuts ? [] : undefined,
                requireAuth: appConfig.requireAuth,
            },
            info,
            measure: mapConfig?.enableMeasure && {},
            exportMap: mapConfig?.enableExportMap && {},
            userLocation: mapConfig?.enableUserLocation && {},
            notes: mapConfig?.enableMapNotes && {},
            search,
            layerSwitcher,
            mapSwitcher,
            activeWidget: mapConfig?.activeWidget,
            nonTranslatableResourceStrings,
            otherLinks: tinkerConfig.links.edges.map((link) => ({
                label: link.node.title,
                url: link.node.url,
            })),
            homeLink: appConfig.homeLink?.url,
        };
    }
    catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.info(`Cannot load tinker config from ${tinkerGraphQLUrl}. %c${message}`, 'color:red');
    }
    return {};
};
//# sourceMappingURL=loadConfigFromTinker.js.map